import { useEffect, useRef, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { api, ApiFailure, type RealtimeResponse } from '../api';
import type { VizColors } from '../theme';
import { Notice } from './Panels';
import { Icon } from './Icon';
import {
  CountryBadge,
  countryName,
  fmtMoney,
  LoadingCard,
  NoWebsite,
  PageHeader,
  SitePicker,
  timeAgo,
  useWebsites,
} from './analytics/shared';

/**
 * Realtime: who is on the site right now and what they are doing.
 *
 * Polls every five seconds while the tab is visible and pauses when it is
 * not, so an open background tab costs nothing. "Right now" means an event
 * in the last five minutes — the same window Plausible and DataFast use.
 */
export function Realtime({ colors, onNavigate }: { colors: VizColors; onNavigate: (page: 'websites' | 'sources') => void }) {
  const sites = useWebsites();
  const [data, setData] = useState<RealtimeResponse | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [now, setNow] = useState(Date.now());
  const seen = useRef<Set<string>>(new Set());
  const [fresh, setFresh] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (sites.selected === '') return;
    let stopped = false;
    seen.current = new Set();
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .getRealtime(sites.selected)
        .then((response) => {
          if (stopped) return;
          const keys = response.realtime.feed.map(feedKey);
          const newOnes = new Set(seen.current.size === 0 ? [] : keys.filter((k) => !seen.current.has(k)));
          keys.forEach((k) => seen.current.add(k));
          setFresh(newOnes);
          setData(response);
          setFailure(null);
        })
        .catch((error: unknown) => {
          if (!stopped) setFailure(error instanceof ApiFailure ? error : new ApiFailure('Lost connection.', 'Retrying…', 0));
        });
    };
    tick();
    const id = window.setInterval(tick, 5000);
    const onVisible = () => document.visibilityState === 'visible' && tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sites.selected]);

  // Keeps "12s ago" labels moving between polls.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (sites.websites === null) return <div className="page"><LoadingCard /></div>;
  if (sites.websites.length === 0) {
    return <NoWebsite what="live visitors" onSetup={() => onNavigate('websites')} onUpload={() => onNavigate('sources')} />;
  }

  const rt = data?.realtime ?? null;

  return (
    <div className="page an-page">
      <PageHeader title="Realtime" subtitle="Updated every 5 seconds. A visitor is live if they did anything in the last 5 minutes.">
        <SitePicker websites={sites.websites} selected={sites.selected} onChange={sites.setSelected} />
      </PageHeader>

      {failure !== null && <Notice tone="warning" message={failure.message} remedy={failure.remedy} />}

      {rt === null ? (
        <LoadingCard label="Connecting…" />
      ) : (
        <>
          <section className="rt-hero">
            <div className="rt-now">
              <span className={rt.visitorsNow > 0 ? 'live-dot on big' : 'live-dot big'} aria-hidden />
              <div>
                <div className="rt-number" aria-live="polite">{rt.visitorsNow}</div>
                <div className="rt-caption">{rt.visitorsNow === 1 ? 'visitor' : 'visitors'} on {data!.website.domain} right now</div>
              </div>
            </div>
            <div className="rt-stats">
              <div><strong>{rt.visitorsLast30}</strong><span>visitors · 30 min</span></div>
              <div><strong>{rt.pageviewsLast30}</strong><span>pageviews · 30 min</span></div>
              <div><strong>{fmtMoney(data!.payments.reduce((s, p) => s + p.amount, 0), { compact: true })}</strong><span>revenue · 24 h</span></div>
            </div>
            <div className="rt-spark" aria-label="Pageviews per minute, last 30 minutes">
              <ResponsiveContainer width="100%" height={72}>
                <BarChart data={rt.perMinute} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} barCategoryGap={2}>
                  <XAxis dataKey="minute" hide />
                  <Tooltip
                    cursor={{ fill: colors.band }}
                    content={({ active, payload }) => {
                      if (active !== true || payload === undefined || payload.length === 0) return null;
                      const p = payload[0]!.payload as RealtimeResponse['realtime']['perMinute'][number];
                      return (
                        <div className="chart-tooltip">
                          <div className="tt-row">
                            <span>{new Date(p.minute).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            <strong>{p.pageviews} views</strong>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="pageviews" fill={colors.series1} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
              <div className="rt-spark-axis"><span>30 min ago</span><span>now</span></div>
            </div>
          </section>

          <div className="panel-grid rt-grid">
            <MiniList title="Active pages" icon="page" rows={rt.activePages} empty="No one is on a page right now." />
            <MiniList title="Sources" icon="link" rows={rt.sources} empty="No active visits." />
            <MiniList
              title="Countries"
              icon="globe"
              rows={rt.countries}
              empty="No active visits."
              render={(key) => (key === 'Unknown' ? 'Unknown' : countryName(key))}
              badge={(key) => <CountryBadge code={key} />}
            />
            <MiniList
              title="Devices"
              icon="device"
              rows={rt.devices}
              empty="No active visits."
              render={(key) => key.charAt(0).toUpperCase() + key.slice(1)}
            />
          </div>

          <div className="rt-columns">
            <section className="card">
              <header>
                <h2><Icon name="realtime" size={15} /> Live feed</h2>
                <span className="sub">last 30 minutes</span>
              </header>
              {rt.feed.length === 0 ? (
                <p className="muted">
                  Quiet for now. Open your site in another tab and watch the visit arrive here.
                </p>
              ) : (
                <ol className="feed">
                  {rt.feed.map((item) => {
                    const key = feedKey(item);
                    return (
                      <li key={key} className={fresh.has(key) ? 'feed-item fresh' : 'feed-item'}>
                        <span className={`feed-kind ${item.kind}`} aria-hidden>
                          <Icon name={item.kind === 'goal' ? 'goal' : item.kind === 'identify' ? 'admin' : 'page'} size={13} />
                        </span>
                        <span className="feed-main">
                          {item.kind === 'goal' ? (
                            <><strong>{item.name}</strong> on {item.path}</>
                          ) : item.kind === 'identify' ? (
                            <>Visitor identified on {item.path}</>
                          ) : (
                            <>Viewed <strong>{item.path}</strong></>
                          )}
                          <span className="feed-meta">
                            {item.country !== null && <><CountryBadge code={item.country} /> </>}
                            {item.source} · {item.browser ?? item.device} · #{item.visitor}
                          </span>
                        </span>
                        <time className="feed-time" dateTime={item.at}>{timeAgo(item.at, now)}</time>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            <section className="card">
              <header>
                <h2><Icon name="revenue" size={15} /> Recent payments</h2>
                <span className="sub">last 24 hours</span>
              </header>
              {data!.payments.length === 0 ? (
                <p className="muted">No payments in the last 24 hours. Connect Stripe, LemonSqueezy, Polar or Shopify on the Websites page.</p>
              ) : (
                <ol className="feed">
                  {data!.payments.map((p, i) => (
                    <li key={`${p.at}-${i}`} className="feed-item">
                      <span className="feed-kind goal" aria-hidden><Icon name="revenue" size={13} /></span>
                      <span className="feed-main">
                        <strong>{fmtMoney(p.amount, { currency: p.currency })}</strong>
                        <span className="feed-meta">via {p.provider}</span>
                      </span>
                      <time className="feed-time" dateTime={p.at}>{timeAgo(p.at, now)}</time>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function feedKey(item: RealtimeResponse['realtime']['feed'][number]): string {
  return `${item.at}|${item.visitor}|${item.kind}|${item.path}|${item.name ?? ''}`;
}

function MiniList({
  title,
  icon,
  rows,
  empty,
  render = (key) => key,
  badge,
}: {
  title: string;
  icon: string;
  rows: { key: string; visitors: number }[];
  empty: string;
  render?: (key: string) => string;
  badge?: (key: string) => JSX.Element;
}) {
  const max = Math.max(1, ...rows.map((r) => r.visitors));
  return (
    <section className="card breakdown">
      <header className="breakdown-head">
        <h2><Icon name={icon} size={15} /> {title}</h2>
      </header>
      {rows.length === 0 ? (
        <div className="breakdown-empty">{empty}</div>
      ) : (
        <ol className="breakdown-list">
          {rows.map((row) => (
            <li key={row.key}>
              <div className="breakdown-row static">
                <span className="breakdown-bar" style={{ width: `${(row.visitors / max) * 100}%` }} aria-hidden />
                <span className="breakdown-key">
                  {badge?.(row.key)}
                  <span className="breakdown-text">{render(row.key)}</span>
                </span>
                <span className="breakdown-value">{row.visitors}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
